// Environment configuration for worker endpoint
export const CONFIG = {
  // Worker endpoint - replace with your actual worker endpoint
  WORKER_ENDPOINT: process.env.NEXT_PUBLIC_WORKER_ENDPOINT || "https://kbnote.it.com",

  // API endpoints that match your backend APIHandler.js
  API_ENDPOINTS: {
    // Core API endpoints
    STATS: "/api/stats",                    // Real-time dashboard statistics
    PERFORMANCE: "/api/performance",        // System performance metrics
    CONFIG: "/api/config",                  // Configuration management (GET/POST)
    HEALTH: "/api/health",                  // System health check
    LOGS: "/api/logs",                      // System logs
    
    // Traffic and monitoring
    LIVE_TRAFFIC: "/api/live-traffic",      // Live traffic feed
    VISITORS: "/api/visitors",              // Live visitors data
    
    // Security and management
    BYPASS: "/api/bypass",                  // Bypass token creation
    IP_MANAGEMENT: "/api/ip-management",    // IP whitelist/blacklist management
    IP_LIST: "/api/ip-list",               // Get current IP lists
    
    // Analytics and intelligence
    CONVERSIONS: "/api/conversions",        // Conversion analytics
    INTELLIGENCE: "/api/intelligence",      // Threat intelligence
    CAMPAIGN_PERFORMANCE: "/api/campaign-performance", // Campaign performance data
    
    // Legacy endpoints (for backward compatibility)
    LOGIN: "/dashboard",                    // Dashboard login (uses access_code param)
    DASHBOARD_DATA: "/api/stats",           // Alias for stats
    BOT_DETECTION: "/api/live-traffic",     // Alias for live traffic
    BYPASS_TOKENS: "/api/bypass",           // Alias for bypass
    CAMPAIGN_STATS: "/api/campaign-performance", // Alias for campaign performance
    SYSTEM_STATUS: "/api/health",           // Alias for health
  },

  // Update intervals (in milliseconds) for real-time features
  UPDATE_INTERVALS: {
    // Increased intervals to prevent rate limiting
    REAL_TIME_TRAFFIC: 50000,       // 10 seconds - Live traffic (was 2s)
    AGGREGATE_STATS: 60000,         // 1 minute - Dashboard stats (was 3s)
    LIVE_VISITORS: 30000,           // 30 seconds - Live visitors (was 5s)
    CONVERSIONS: 30000,             // 30 seconds - Conversion data (was 10s)
    PERFORMANCE: 120000,            // 2 minutes - Performance metrics (was 30s)
    CONFIG_SYNC: 300000,            // 5 minutes - Configuration sync (was 15s)
    HEALTH_CHECK: 180000,           // 3 minutes - Health check (was 1m)
    INTELLIGENCE: 300000,           // 5 minutes - Intelligence data (was 2m)
    CAMPAIGN_STATS: 180000,         // 3 minutes - Campaign performance (was 1m)
    LOGS: 120000,                   // 2 minutes - System logs (was 30s)
    
    // Legacy support (mapped to new values)
    REAL_TIME_STATS: 60000,         // Alias for AGGREGATE_STATS
    BOT_DETECTION: 300000,          // Alias for INTELLIGENCE
  },

  // Rate limiting configuration
  RATE_LIMITING: {
    // Per-endpoint rate limits (minimum milliseconds between requests)
    ENDPOINTS: {
      ["/api/stats"]: 30000,              // 30 seconds minimum
      ["/api/live-traffic"]: 10000,       // 10 seconds minimum
      ["/api/visitors"]: 15000,           // 15 seconds minimum
      ["/api/performance"]: 60000,        // 1 minute minimum
      ["/api/intelligence"]: 120000,      // 2 minutes minimum
      ["/api/conversions"]: 60000,        // 1 minute minimum
      ["/api/campaign-performance"]: 120000, // 2 minutes minimum
      ["/api/config"]: 300000,            // 5 minutes minimum (GET)
      ["/api/health"]: 60000,             // 1 minute minimum
      ["/api/logs"]: 60000,               // 1 minute minimum
    },
    
    // Global rate limiting
    GLOBAL_MIN_INTERVAL: 5000,            // 5 seconds minimum between any requests
    MAX_CONCURRENT_REQUESTS: 3,           // Maximum concurrent requests
    RETRY_AFTER_RATE_LIMIT: 30000,       // Wait 30 seconds after rate limit hit
    
    // Exponential backoff configuration
    BACKOFF: {
      INITIAL_DELAY: 5000,                // Start with 5 second
      MAX_DELAY: 60000,                   // Maximum 1 minute delay
      MULTIPLIER: 2,                      // Double delay each time
      JITTER: true,                       // Add random jitter to prevent thundering herd
    }
  },

  // Caching configuration
  CACHE: {
    // Per-endpoint cache TTL (time to live in milliseconds)
    TTL: {
      ["/api/stats"]: 30000,              // 30 seconds
      ["/api/live-traffic"]: 10000,           // No cache for live data
      ["/api/visitors"]: 10000,               // No cache for live data
      ["/api/performance"]: 120000,       // 2 minutes
      ["/api/intelligence"]: 300000,      // 5 minutes
      ["/api/conversions"]: 180000,       // 3 minutes
      ["/api/campaign-performance"]: 300000, // 5 minutes
      ["/api/config"]: 600000,            // 10 minutes
      ["/api/health"]: 60000,             // 1 minute
      ["/api/logs"]: 120000,              // 2 minutes
    },
    
    // Cache settings
    MAX_CACHE_SIZE: 50,                   // Maximum cached entries
    ENABLE_CACHE: true,                   // Enable/disable caching
    AUTO_CLEANUP: true,                   // Auto cleanup expired entries
    CLEANUP_INTERVAL: 300000,             // Cleanup every 5 minutes
  },

  // WebSocket configuration (if implemented)
  WEBSOCKET: {
    ENABLED: false,             // Enable WebSocket for real-time updates
    RECONNECT_INTERVAL: 10000,  // 10 seconds - Increased reconnection interval
    MAX_RECONNECT_ATTEMPTS: 3,  // Reduced reconnection attempts
    HEARTBEAT_INTERVAL: 30000,  // 30 seconds - WebSocket heartbeat
    CHANNELS: {
      TRAFFIC: "traffic",       // Live traffic updates
      CONVERSIONS: "conversions", // Conversion updates
      ALERTS: "alerts",         // Security alerts
      STATS: "stats",           // Real-time stats
    }
  },

  // Dashboard configuration
  DASHBOARD: {
    ACCESS_CODE_PARAM: "access_code",     // URL parameter for access code
    DEFAULT_ACCESS_CODE: "0rwef8u",       // Default access code (should be changed)
    SESSION_TIMEOUT: 3600000,             // 1 hour session timeout
    AUTO_REFRESH: true,                   // Enable auto-refresh
    CHART_ANIMATION: true,                // Enable chart animations
    
    // Performance settings
    MAX_CHART_POINTS: 50,                 // Maximum data points in charts
    ANIMATION_DURATION: 300,              // Animation duration in ms
    DEBOUNCE_DELAY: 1000,                  // Input debounce delay
    
    // Auto-refresh settings
    PAUSE_ON_ERROR: true,                 // Pause auto-refresh on errors
    PAUSE_DURATION: 60000,                // Pause for 1 minute on errors
    MAX_ERROR_COUNT: 5,                   // Max errors before longer pause
  },

  // API configuration
  API: {
    TIMEOUT: 15000,             // 15 seconds - Increased API request timeout
    RETRY_ATTEMPTS: 2,          // Reduced retry attempts to prevent rate limiting
    RETRY_DELAY: 2000,          // 2 seconds - Increased delay between retries
    CACHE_DURATION: 60000,      // 1 minute - Increased client-side cache duration
    
    // Connection settings
    KEEP_ALIVE: false,          // Disable keep-alive to prevent connection pooling issues
    MAX_REDIRECTS: 3,           // Maximum redirects to follow
    
    // Error handling
    RETRY_ON_TIMEOUT: true,     // Retry on timeout errors
    RETRY_ON_NETWORK_ERROR: true, // Retry on network errors
    RETRY_ON_RATE_LIMIT: false, // Don't retry immediately on rate limit
    
    // Request headers
    DEFAULT_HEADERS: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      'X-Requested-With': 'XMLHttpRequest'
    }
  },

  // Security thresholds (default values)
  SECURITY: {
    MODES: {
      very_fast: {
        name: "Easy Mode",
        allowThreshold: 70,
        challengeThreshold: 89,
        blockThreshold: 90,
        description: "Maximum speed (~5-15ms), minimal false positives"
      },
      medium: {
        name: "Medium Mode", 
        allowThreshold: 40,
        challengeThreshold: 74,
        blockThreshold: 75,
        description: "Balanced security & speed (~15-30ms)"
      },
      hard: {
        name: "Hard Mode",
        allowThreshold: 25,
        challengeThreshold: 49,
        blockThreshold: 50,  
        description: "Maximum security (~30-60ms), may have false positives"
      },
      easy: {
        name: "Easy Mode",
        allowThreshold: 60,
        challengeThreshold: 85,
        blockThreshold: 100,
        description: "Maximum speed (~10-30ms), minimal false positives"
      },
      strict: {
        name: "Strict Mode",
        allowThreshold: 25,
        challengeThreshold: 50,
        blockThreshold: 85,
        description: "Maximum security (~60-100ms), may have false positives"
      }
    }
  },

  // Feature flags
  FEATURES: {
    REAL_TIME_MONITORING: true,     // Enable real-time monitoring
    INTELLIGENCE_ENGINE: true,      // Enable threat intelligence
    CAMPAIGN_ROUTING: true,         // Enable campaign routing
    GEO_ROUTING: true,             // Enable geographic routing
    DEVICE_ROUTING: true,          // Enable device-based routing
    A_B_TESTING: true,             // Enable A/B testing
    BEHAVIOR_TRACKING: true,       // Enable behavior tracking
    HONEYPOT_SYSTEM: false,        // Enable honeypot system
    WEBHOOK_NOTIFICATIONS: true,   // Enable webhook notifications
    RATE_LIMITING: true,           // Enable rate limiting
    IP_MANAGEMENT: true,           // Enable IP whitelist/blacklist
    BYPASS_SYSTEM: true,           // Enable bypass token system
    
    // New features
    SMART_CACHING: true,           // Enable smart caching
    ERROR_RECOVERY: true,          // Enable automatic error recovery
    PERFORMANCE_MONITORING: true,  // Enable performance monitoring
    CONNECTION_POOLING: false,     // Disable connection pooling for now
  },

  // Default admin credentials (CHANGE THESE IN PRODUCTION!)
  DEFAULT_ADMIN: {
    USERNAME: "admin",
    PASSWORD: "gajah2024!",
  },

  // System information
  SYSTEM: {
    NAME: "🐘⚡ Gajah Terbang",
    VERSION: "2.0.1",              // Incremented version
    DESCRIPTION: "Advanced Anti-Bot & Campaign Management System",
    AUTHOR: "Gajah Terbang Security",
    BUILD_DATE: new Date().toISOString(),
  },

  // Enhanced error messages with solutions
  ERRORS: {
    NETWORK_ERROR: "Network connection failed. Please check your internet connection and try again.",
    API_ERROR: "API request failed. The server may be temporarily unavailable.",
    CONFIG_ERROR: "Configuration update failed. Please verify your settings and permissions.",
    PERMISSION_ERROR: "Access denied. Please check your access code and permissions.",
    TIMEOUT_ERROR: "Request timed out. The server may be experiencing high load.",
    INVALID_INPUT: "Invalid input provided. Please check your data format.",
    RATE_LIMIT_ERROR: "Too many requests. Please wait a moment before trying again.",
    SERVER_ERROR: "Internal server error. Please contact support if this persists.",
    CONNECTION_ERROR: "Unable to connect to server. Please check the endpoint configuration.",
    
    // Error codes for programmatic handling
    CODES: {
      NETWORK_ERROR: 'NETWORK_ERROR',
      TIMEOUT_ERROR: 'TIMEOUT_ERROR',
      RATE_LIMIT_ERROR: 'RATE_LIMIT_ERROR',
      SERVER_ERROR: 'SERVER_ERROR',
      PERMISSION_ERROR: 'PERMISSION_ERROR',
      CONFIG_ERROR: 'CONFIG_ERROR',
    }
  },

  // UI Configuration
  UI: {
    THEME: "default",
    ANIMATIONS_ENABLED: true,
    DARK_MODE: false,
    COMPACT_MODE: false,
    SHOW_ADVANCED_OPTIONS: false,
    
    // Performance settings
    VIRTUAL_SCROLLING: true,        // Enable virtual scrolling for large lists
    LAZY_LOADING: true,             // Enable lazy loading for heavy components
    THROTTLE_UPDATES: true,         // Throttle rapid updates
    
    // Notification settings
    SHOW_SUCCESS_NOTIFICATIONS: true,
    SHOW_ERROR_NOTIFICATIONS: true,
    NOTIFICATION_DURATION: 5000,    // 5 seconds
    MAX_NOTIFICATIONS: 3,           // Maximum simultaneous notifications
  },

  // Development and debugging
  DEBUG: {
    ENABLED: process.env.NODE_ENV === 'development',
    LOG_API_CALLS: process.env.NODE_ENV === 'development',
    LOG_CACHE_HITS: false,
    LOG_RATE_LIMITS: true,
    VERBOSE_ERRORS: process.env.NODE_ENV === 'development',
    
    // Performance monitoring
    MONITOR_PERFORMANCE: true,
    LOG_SLOW_REQUESTS: true,
    SLOW_REQUEST_THRESHOLD: 2000,   // 2 seconds
  },

  // Health check configuration
  HEALTH_CHECK: {
    ENABLED: true,
    ENDPOINTS_TO_CHECK: [
      '/api/health',
      '/api/stats'
    ],
    CHECK_INTERVAL: 300000,         // 5 minutes
    FAILURE_THRESHOLD: 3,           // Mark as unhealthy after 3 failures
    SUCCESS_THRESHOLD: 2,           // Mark as healthy after 2 successes
  }
}

// Computed configurations based on environment
const computeConfig = () => {
  // Adjust intervals based on environment
  if (process.env.NODE_ENV === 'development') {
    // Longer intervals in development to prevent rate limiting during testing
    CONFIG.UPDATE_INTERVALS.REAL_TIME_TRAFFIC = 15000  // 15 seconds
    CONFIG.UPDATE_INTERVALS.AGGREGATE_STATS = 120000   // 2 minutes
    CONFIG.API.TIMEOUT = 30000                         // 30 seconds
  }

  // Production optimizations
  if (process.env.NODE_ENV === 'production') {
    CONFIG.FEATURES.SMART_CACHING = true
    CONFIG.CACHE.ENABLE_CACHE = true
    CONFIG.DEBUG.ENABLED = false
    CONFIG.DEBUG.LOG_API_CALLS = false
  }

  return CONFIG
}

// Apply computed configurations
const FINAL_CONFIG = computeConfig()

// Export individual sections for easier access
export const UPDATE_INTERVALS = FINAL_CONFIG.UPDATE_INTERVALS
export const API_ENDPOINTS = FINAL_CONFIG.API_ENDPOINTS
export const SECURITY_MODES = FINAL_CONFIG.SECURITY.MODES
export const FEATURES = FINAL_CONFIG.FEATURES
export const ERRORS = FINAL_CONFIG.ERRORS
export const RATE_LIMITING = FINAL_CONFIG.RATE_LIMITING
export const CACHE = FINAL_CONFIG.CACHE

// Utility functions
export const getEndpointRateLimit = (endpoint: string): number => {
  return FINAL_CONFIG.RATE_LIMITING.ENDPOINTS[endpoint] || FINAL_CONFIG.RATE_LIMITING.GLOBAL_MIN_INTERVAL
}

export const getCacheTTL = (endpoint: string): number => {
  return FINAL_CONFIG.CACHE.TTL[endpoint] || 30000
}

export const isFeatureEnabled = (feature: keyof typeof FINAL_CONFIG.FEATURES): boolean => {
  return FINAL_CONFIG.FEATURES[feature] === true
}

export const getErrorMessage = (errorCode: string): string => {
  return FINAL_CONFIG.ERRORS[errorCode as keyof typeof FINAL_CONFIG.ERRORS] || FINAL_CONFIG.ERRORS.API_ERROR
}

// Export final config as default
export default FINAL_CONFIG
